import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Users } from "lucide-react";

const STATUS_DOT = {
  tentative: "bg-amber-400", confirmed: "bg-emerald-500", checked_in: "bg-sky-500",
  checked_out: "bg-zinc-400", cancelled: "bg-red-400",
};

// Event-type colour coding. Matched by keyword so admin-added custom types
// still land in a sensible bucket. Weddings → orange, Leisure → green,
// Corporate → blue, everything else → slate.
const EVENT_COLORS = {
  wedding: { chip: "bg-orange-100 text-orange-900 border-orange-300 hover:bg-orange-200", legend: "bg-orange-400", label: "Wedding" },
  leisure: { chip: "bg-green-100 text-green-900 border-green-300 hover:bg-green-200", legend: "bg-green-500", label: "Leisure" },
  corporate: { chip: "bg-blue-100 text-blue-900 border-blue-300 hover:bg-blue-200", legend: "bg-blue-500", label: "Corporate" },
  other: { chip: "bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200", legend: "bg-slate-400", label: "Other" },
};
const eventBucket = (type) => {
  const t = (type || "").toLowerCase();
  if (/wedding|honeymoon|marriage|sangeet|reception|anniversary/.test(t)) return "wedding";
  if (/corporate|business|mice|conference|offsite|company|team/.test(t)) return "corporate";
  if (/leisure|holiday|getaway|family|vacation|staycation|social/.test(t)) return "leisure";
  return "other";
};
const eventColor = (type) => EVENT_COLORS[eventBucket(type)];

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const BookingsCalendar = ({ bookings, labelOf, onSelect }) => {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const grid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - first.getDay()); // back to Sunday
    const days = [];
    for (let i = 0; i < 42; i++) { const d = new Date(start); d.setDate(start.getDate() + i); days.push(d); }
    return days;
  }, [month]);

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.check_in_date || !b.check_out_date) continue;
      // mark every day of the stay (inclusive)
      let cur = new Date(b.check_in_date + "T00:00:00");
      const end = new Date(b.check_out_date + "T00:00:00");
      let guard = 0;
      while (cur <= end && guard < 400) {
        const key = iso(cur);
        (map[key] = map[key] || []).push(b);
        cur.setDate(cur.getDate() + 1);
        guard++;
      }
    }
    return map;
  }, [bookings]);

  const todayIso = iso(new Date());
  const title = month.toLocaleDateString([], { month: "long", year: "numeric" });

  return (
    <div data-testid="bookings-calendar">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        <div className="flex gap-1">
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button size="sm" variant="outline" className="h-8" onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }} data-testid="cal-today">Today</Button>
          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-t border-l border-zinc-200 rounded-md overflow-hidden bg-white">
        {DOW.map((d) => <div key={d} className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 px-2 py-1.5 border-r border-b border-zinc-200 bg-zinc-50">{d}</div>)}
        {grid.map((day, i) => {
          const key = iso(day);
          const inMonth = day.getMonth() === month.getMonth();
          const items = byDay[key] || [];
          return (
            <div key={i} className={`min-h-[104px] border-r border-b border-zinc-200 p-1 ${inMonth ? "bg-white" : "bg-zinc-50/60"}`} data-testid={`cal-day-${key}`}>
              <div className={`text-[11px] font-semibold mb-1 ${key === todayIso ? "text-white bg-zinc-900 rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "text-zinc-700" : "text-zinc-300"}`}>{day.getDate()}</div>
              <div className="space-y-0.5">
                {items.slice(0, 3).map((b) => {
                  const ec = eventColor(b.booking_type);
                  const typeLabel = b.booking_type ? (labelOf ? labelOf("booking_type", b.booking_type) : b.booking_type) : ec.label;
                  return (
                    <button key={b.id} onClick={() => onSelect(b)}
                      title={`${b.client_name} · ${typeLabel}${b.num_pax ? " · " + b.num_pax + " pax" : ""}${b.villa ? " · " + (labelOf ? labelOf("villa", b.villa) : b.villa) : ""}`}
                      className={`w-full text-left flex items-center gap-1 text-[10px] rounded border px-1 py-0.5 ${ec.chip} transition-colors`}
                      data-testid={`cal-booking-${b.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[b.status] || "bg-zinc-400"}`} title={b.status} />
                      <span className="truncate font-medium flex-1">{b.client_name}</span>
                      {b.num_pax ? (
                        <span className="shrink-0 inline-flex items-center gap-0.5 opacity-80 font-semibold tabular-nums">
                          <Users className="w-2.5 h-2.5" />{b.num_pax}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
                {items.length > 3 && <div className="text-[9px] text-zinc-400 px-1">+{items.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-[11px] text-zinc-500">
        <span className="font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">Event</span>
        {["wedding", "leisure", "corporate", "other"].map((k) => (
          <span key={k} className="flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-sm ${EVENT_COLORS[k].legend}`} /> {EVENT_COLORS[k].label}</span>
        ))}
        <span className="w-px h-3 bg-zinc-200 mx-1" />
        <span className="font-semibold text-zinc-600 uppercase tracking-wider text-[10px]">Status</span>
        {Object.entries({ tentative: "Tentative", confirmed: "Confirmed", checked_in: "Checked-in", checked_out: "Checked-out", cancelled: "Cancelled" }).map(([k, l]) => (
          <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${STATUS_DOT[k]}`} /> {l}</span>
        ))}
      </div>
    </div>
  );
};
